import { useRef, useState, useEffect } from 'react'
import { Modal, Button, Box, Text, ActionIcon, Group, Paper, Alert } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconQrcode, IconX, IconAlertCircle } from '@tabler/icons-react'
import { BrowserMultiFormatReader } from '@zxing/browser'
import { NotFoundException } from '@zxing/library'

interface Props {
  onScanned: (value: string) => void
  label?: string
  size?: 'xs' | 'sm' | 'md'
}

export default function CameraScanner({ onScanned, label = 'Scan', size = 'xs' }: Props) {
  const [opened, { open, close }] = useDisclosure(false)
  const [scanning, setScanning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const scannerRef = useRef<any>(null)
  const decodedRef = useRef(false)

  const stopScanner = () => {
    try {
      if (scannerRef.current) {
        BrowserMultiFormatReader.releaseAllStreams()
        scannerRef.current = null
      }
      if (videoRef.current) videoRef.current.srcObject = null
    } catch {}
    setScanning(false)
  }

  const startScanner = async () => {
    setError(null)
    decodedRef.current = false
    const videoElement = videoRef.current
    if (!videoElement) {
      setError('ไม่พบองค์ประกอบกล้อง กรุณาลองใหม่')
      return
    }
    try {
      const codeReader = new BrowserMultiFormatReader()
      scannerRef.current = codeReader
      setScanning(true)
      await codeReader.decodeFromConstraints(
        { video: { facingMode: 'environment' } },
        videoElement,
        (result, err) => {
          if (result && !decodedRef.current) {
            decodedRef.current = true
            const text = result.getText().trim()
            stopScanner()
            onScanned(text)
            close()
          }
          if (err && !(err instanceof NotFoundException)) {
            // ignore continuous no-result errors
          }
        }
      )
    } catch (err: any) {
      setScanning(false)
      if (err?.name === 'NotAllowedError') {
        setError('กรุณาอนุญาตการเข้าถึงกล้อง')
      } else if (err?.name === 'NotFoundError') {
        setError('ไม่พบกล้องในอุปกรณ์นี้')
      } else {
        notifications.show({ color: 'red', message: 'Cannot open camera' })
        setError('ไม่สามารถเปิดกล้องได้')
      }
    }
  }

  const handleOpen = () => {
    open()
  }

  const handleClose = () => {
    stopScanner()
    close()
  }

  // เริ่มกล้องเมื่อ modal เปิดและ video element ถูก mount แล้วเสมอ (ไม่พึ่ง setTimeout)
  useEffect(() => {
    if (opened) {
      startScanner()
    } else {
      stopScanner()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened])

  useEffect(() => () => { stopScanner() }, [])

  return (
    <>
      <ActionIcon
        size={size === 'xs' ? 26 : 32}
        variant="subtle"
        color="teal"
        onClick={handleOpen}
        title="Scan with camera"
      >
        <IconQrcode size={size === 'xs' ? 14 : 18} />
      </ActionIcon>

      <Modal
        opened={opened}
        onClose={handleClose}
        size="sm"
        radius="lg"
        withCloseButton={false}
        title={
          <Group justify="space-between" w="100%">
            <Group gap={8}>
              <IconQrcode size={16} color="#0d9488" />
              <Text fw={600} size="sm">Scan QR / Barcode</Text>
            </Group>
            <ActionIcon variant="subtle" color="gray" onClick={handleClose}>
              <IconX size={14} />
            </ActionIcon>
          </Group>
        }
      >
        <Box>
          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" mb={8}>
              {error}
              <Button size="xs" mt="xs" onClick={startScanner}>ลองใหม่</Button>
            </Alert>
          )}

          <Text size="xs" c="dimmed" mb={12} ta="center">
            Point camera at QR code or barcode
          </Text>
          {/* Camera container — video element มักจะ render เสมอเพื่อกัน ref เป็น null ตอนเริ่มสแกน */}
          <Paper
            radius="md"
            style={{ width: '100%', minHeight: 280, overflow: 'hidden', background: '#0f172a' }}
          >
            <video
              ref={videoRef}
              style={{ width: '100%', height: 280, display: 'block', objectFit: 'cover' }}
              autoPlay muted playsInline
            />
          </Paper>
          {!scanning && !error && (
            <Text size="xs" c="dimmed" ta="center" mt={8}>
              Starting camera...
            </Text>
          )}
          <Button variant="light" color="gray" fullWidth mt={12} onClick={handleClose}>
            Cancel
          </Button>
        </Box>
      </Modal>
    </>
  )
}
